import { useSnackbar } from 'notistack'

import { ActionMenuItem } from '../ActionMenuItem/ActionMenuItem'
import { useActionMenuContext } from '../context/ActionMenuContext'
import { MenuItemLike, WithSnackbarMessages } from '../index.types'

type Props<Data extends Record<string, unknown>> = MenuItemLike & {
  onClick: (row: Data) => Promise<void> | void
  icon?: React.ReactElement
  text: string
} & WithSnackbarMessages<Data>

/** Component to be used as a base for creating new ActionMenuSnackbarItem. NOT for standalone use (eg. ActionCopyItem) */
export const ActionMenuSnackbarItem = <Data extends Record<string, unknown>>({
  displaySeparately,
  errorMessage = 'Failed to complete action',
  successMessage = 'Action completed successfully',
  onClick,
  overrideText,
  icon,
  text
}: Props<Data>) => {
  const { enqueueSnackbar } = useSnackbar()
  const { row, closeMenu } = useActionMenuContext<Data>()

  const handleClick = async () => {
    try {
      await onClick(row)
      enqueueSnackbar(typeof successMessage === 'function' ? successMessage(row) : successMessage, {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(typeof errorMessage === 'function' ? errorMessage(row) : errorMessage, {
        variant: 'error'
      })
    } finally {
      closeMenu?.()
    }
  }

  return (
    <ActionMenuItem
      displaySeparately={displaySeparately}
      text={overrideText || text}
      icon={icon}
      onClick={handleClick}
    />
  )
}
